import get from 'lodash/get'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { AuthUserProfile } from 'types/auth'
import { IFilter, Where } from 'types/common'
import { AggregationPipeline } from 'types/common/aggregation'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { searchGlobal } from 'API/search'
import {
  adminChangePassword,
  bindWorkEmail,
  countUsers as countUsersAPI,
  countUsersAuth,
  getRecentlyViewed,
  getUserById,
  getUsers as getUsersAPI,
  getUsersAggregate,
  resendWorkEmailVerification,
  updateUserById,
  verifyWorkEmail
} from 'API/user'
import { getUsersFilterPipeline } from 'components/Pages/UserPage/UserList/query'
import { ESortingUserOptions } from 'components/Pages/UserPage/UserListFilterDialog/constants'
import { AuthRoleIdEnum } from 'constants/user'
import { IGroupMember, IGroupMemberPermissionId } from 'interfaces/group'
import { IAuditWithRelations, ICreateEditUserRequest, IUser, IUserWithRelations } from 'interfaces/user'
import { getValidArray } from 'utils/common'
import { getFullName } from 'utils/users'
import { GlobalSearchDto } from './searchStore'
import { getError } from 'utils/getError'
import { EmailVerificationResponse, BackendSuccessResponse } from 'interfaces/common'

class UserStore {
  rootStore: RootStore

  currentUser: AuthUserProfile = {} as AuthUserProfile
  currentUserGroupMembers: IGroupMember[] = []

  users: IUserWithRelations[] = []
  numberOfUsers: number = 0

  usersWithOutPagination: IUserWithRelations[] = []

  isLoading: boolean = false

  filter: IFilter<IUser> = {}

  isManageModeInUserDetail = false
  canUserEditInUserDetail = true

  userDetail: IUserWithRelations = {}
  selectedUserDetail: IUserWithRelations = {}
  recentAudits: IAuditWithRelations[] = []
  isManageMode: boolean = false

  userDetailProcesses: IEsGlobalSearchItem[] = []
  countDetailProcess: number = 0

  userDetailCollections: IEsGlobalSearchItem[] = []
  countDetailCollection: number = 0

  // Email verification loading state
  isEmailVerifying: boolean = false

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public setManageModeInUserDetail(isManageMode: boolean) {
    this.isManageModeInUserDetail = isManageMode ?? false
  }

  public setCanUserEditUserDetail(canEdit: boolean) {
    this.canUserEditInUserDetail = canEdit
  }

  public setCurrentUser(user: AuthUserProfile) {
    const haveEditorRole =
      this.currentUserGroupMembers.some(
        groupMember =>
          Number(groupMember?.permissionId ?? IGroupMemberPermissionId.VIEWER) < IGroupMemberPermissionId.VIEWER
      ) || user?.authRoleId < AuthRoleIdEnum.BASIC_USER
    this.currentUser = { ...user, isEditor: haveEditorRole }
  }

  public setCurrentUserGroupMembers(groupMembers: IGroupMember[]) {
    this.currentUserGroupMembers = groupMembers
  }

  public async getUserDetail(userId: number, filter?: IFilter<IUserWithRelations>, isSelected?: boolean) {
    if (!isSelected) {
      this.isLoading = true
    }

    if (isSelected) {
      this.selectedUserDetail = await getUserById(userId, filter)
    } else {
      const userDetail = await getUserById(userId, filter)
      this.userDetail = {
        ...userDetail,
        fullName: getFullName(userDetail?.firstName, userDetail?.lastName),
        image: userDetail?.image ?? ''
      }
    }

    if (!isSelected) {
      this.isLoading = false
    }
  }

  public async setUserListFilter(filter: IFilter<IUser & { keyword?: string }> = {}): Promise<void> {
    this.filter = filter
    await Promise.all([this.getUsers(filter), this.countUsers(filter?.where)])
  }

  public async refetchUserList(): Promise<void> {
    await Promise.all([this.getUsers(this.filter), this.countUsers(this.filter?.where)])
  }

  public async getUsers(filter: IFilter<IUser> = {}): Promise<void> {
    this.users = await getUsersAPI(filter)
  }

  public async countUsers(where: Where<IUser> = {}): Promise<number> {
    this.numberOfUsers = await countUsersAPI(where)
    return this.numberOfUsers
  }

  public async checkExistUser(where: Where<IUser> = {}): Promise<number> {
    this.numberOfUsers = await countUsersAuth(where)
    return this.numberOfUsers
  }

  public async updateUser(userId: number, userData: IUser = {}): Promise<void> {
    this.isLoading = true
    await updateUserById(userId, userData)
    this.isLoading = false
  }

  public async createUser(userData: ICreateEditUserRequest = {}): Promise<void> {
    this.isLoading = true
    await this.createUser(userData)
    this.isLoading = false
  }

  public async adminChangePassword(userId: number, newPassword: string): Promise<void> {
    this.isLoading = true
    await adminChangePassword({ userId, newPassword })
    this.isLoading = false
  }

  public async fetchRecentlyViewed(): Promise<void> {
    this.isLoading = true
    try {
      const result = await getRecentlyViewed()
      this.recentAudits = result || []
    } catch (error) {
      console.error('Failed to fetch recently viewed items:', error)
      this.recentAudits = []
    }
    this.isLoading = false
  }

  public async getUsersByPipeline(pipeline: AggregationPipeline = []): Promise<void> {
    this.isLoading = true
    const dataResponse = await getUsersAggregate(pipeline)

    this.users = getValidArray(dataResponse?.results)
    this.numberOfUsers = dataResponse?.totalCount ?? 0
    this.isLoading = false
  }

  public async getUsersForExport(): Promise<IUserWithRelations[]> {
    const organizationId = this.rootStore.authStore.userDetail?.organizationId
    const pipeline = getUsersFilterPipeline(
      organizationId,
      0,  // No limit - fetch all users
      0,  // No skip - start from beginning
      '', // No keyword filter
      false,
      ESortingUserOptions.NAME
    )
    //* INFO: Modify the pipeline to get the total src\components\Pages\UserPage\UserList\query.ts
    const facetResults = get(pipeline, `[${pipeline?.length - 1}].$facet.results`) || []
    const projection = facetResults?.[facetResults?.length - 1]?.['$project']
    const newPipeline: AggregationPipeline = pipeline.slice(0, pipeline.length - 1)
    newPipeline.push({ $facet: { results: [{ $project: projection }] } })
    const dataResponseWithOutPagination = await getUsersAggregate(newPipeline)

    const users: IUserWithRelations[] = getValidArray(dataResponseWithOutPagination?.results)
    this.usersWithOutPagination = users
    return users
  }
  public setManageMode = (isManageMode: boolean): void => {
    this.isManageMode = isManageMode
  }

  public setDefaultUserListFilter(organizationId: number, pageIndex: number, pageSize: number): void {
    this.filter = {
      where: {
        organizationId,
        isInactive: false
      },
      include: ['authRole', 'groupMembers'],
      skip: (pageIndex - 1) * pageSize,
      limit: pageSize,
      order: ['updatedAt DESC']
    }
  }

  public async getDetailProcess(filter: IFilter<GlobalSearchDto>): Promise<void> {
    try {
      this.isLoading = true

      const searchResult = await searchGlobal(filter)
      this.userDetailProcesses = searchResult.data
      this.countDetailProcess = searchResult.total

      this.isLoading = false
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 184 Log ~ UserStore Log error', error)
    }
  }

  public async getDetailCollection(filter: IFilter<GlobalSearchDto>): Promise<void> {
    try {
      this.isLoading = true

      const searchResult = await searchGlobal(filter)
      this.userDetailCollections = searchResult.data
      this.countDetailCollection = searchResult.total

      this.isLoading = false
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 184 Log ~ UserStore Log error', error)
    }
  }

  public clearStore(): void {
    this.userDetail = {} as IUserWithRelations
    this.userDetailProcesses = []
    this.countDetailProcess = 0
  }

  // Email verification methods
  public async bindWorkEmail(workEmail: string): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await bindWorkEmail(workEmail)
      await this.rootStore.authStore.getMyUser()
      return { success: true, message: response?.message || 'Email verification code sent successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 237 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }

  public async verifyWorkEmail(token: string): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await verifyWorkEmail(token)
      await this.rootStore.authStore.getMyUser()
      return { success: true, message: response?.message || 'Email verified successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 247 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }

  public async resendWorkEmailVerification(): Promise<EmailVerificationResponse> {
    this.isEmailVerifying = true
    try {
      const response: BackendSuccessResponse = await resendWorkEmailVerification()
      return { success: true, message: response?.message || 'Verification code resent successfully.' }
    } catch (error: any) {
      console.log('🚀 Log ~ file: userStore.ts Log ~ line 255 Log ~ UserStore Log error', error)
      return { success: false, message: getError(error) }
    } finally {
      this.isEmailVerifying = false
    }
  }
}

export default UserStore
